import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Form,
  Grid,
  InlineStack,
  Link,
  Paragraph,
  Select,
  Spinner,
  Stack,
  Text,
  TextField,
  Tooltip,
  type BoxProps,
  type StackProps,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import {
  buildPushTestRequest,
  extractPushApiErrors,
  PUSH_TEST_BODY,
  PUSH_TEST_TITLE,
  sendTestPush,
} from '../api/verify-push';
import type { PushApiError, PushPlatform, PushProvider } from '../types';
import {
  matchesLastTestedRecipient,
  readLastTestedRecipient,
  writeLastTestedRecipient,
  type LastTestedRecipient,
} from '../utils/last-tested-recipient';
import { PLATFORM_LABELS } from '../utils/platforms';
import { useNeedsEmailConfirmation } from '../hooks';
import { PhonePreview } from './phone-preview';

const DEVICE_TOKEN_HELP_URL = 'https://docs.customer.io/journeys/push-getting-started/#how-to-find-your-device-tokens';

const PLATFORM_OPTIONS = [
  { value: 'android', label: PLATFORM_LABELS.android },
  { value: 'ios', label: PLATFORM_LABELS.ios },
];

export type PushSendTestProps = {
  workspaceId: number;
  /** The platform the page is configuring; the initial device type for the test. */
  platform: PushPlatform;
  /** The provider the test is sent through. */
  provider: PushProvider;
  /** Whether the test goes to the provider's development endpoint. */
  sandbox: boolean;
  /** How many platforms the workspace has configured; more than one lets the sender choose. */
  configuredPlatformCount: number;
  /**
   * Supplies the credential to test with while the provider is not yet saved.
   * `null` once it is: the backend then reads the stored credential.
   */
  getCredentials: (() => Promise<string>) | null;
  /** Locks the whole form (a viewer who cannot edit, or a save in flight). */
  isDisabled: boolean;
  /** Hands the errors from a failed send to whatever displays them. */
  onErrors: (errors: PushApiError[]) => void;
} & BoxProps;

/**
 * Sends one test push to a device token and reports how it went.
 *
 * The last token tested from this workspace is remembered in the browser, so a
 * repeat test is a single checkbox rather than a paste. The device type chosen
 * here scopes that memory; the credential the test uses is decided by the
 * provider the page is configuring, so the device type is not sent with it.
 */
export function PushSendTest({
  workspaceId,
  platform,
  provider,
  sandbox,
  configuredPlatformCount,
  getCredentials,
  isDisabled,
  onErrors,
  ...boxProps
}: PushSendTestProps) {
  const [recipient, setRecipient] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<PushPlatform | null>(platform);
  const [lastTested, setLastTested] = useState(() => readLastTestedRecipient(workspaceId));
  const [errors, setErrors] = useState<PushApiError[]>([]);

  const needsEmailConfirmation = useNeedsEmailConfirmation();
  const canChoosePlatform = configuredPlatformCount > 1;

  const reportErrors = (next: PushApiError[]) => {
    setErrors(next);
    onErrors(next);
  };

  const sendTest = useMutation({
    mutationFn: async () => {
      const credentials = await resolveCredentials(getCredentials);
      return sendTestPush(workspaceId, buildPushTestRequest({ to: recipient, provider, sandbox, credentials }));
    },
    onMutate: () => {
      reportErrors([]);
    },
    onSuccess: () => {
      const tested = rememberedRecipient(recipient, selectedPlatform, sandbox);
      writeLastTestedRecipient(workspaceId, tested);
      setLastTested(tested);
    },
    onError: (error: unknown) => {
      reportErrors(extractPushApiErrors(error));
    },
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await sendTest.mutateAsync();
    } catch {
      // Already reported through onErrors; the status line says the send failed.
    }
  };

  const selectLastTested = (checked: boolean) => {
    const chosen = checked ? lastTested : null;
    setRecipient(chosen == null ? '' : chosen.id);
    setSelectedPlatform(nextPlatform(chosen, canChoosePlatform, selectedPlatform));
  };

  const blockedReason = sendBlockedReason(needsEmailConfirmation, selectedPlatform, recipient, configuredPlatformCount);
  const isSending = sendTest.isPending;
  const isLocked = isSending || isDisabled;

  return (
    <Box {...boxProps}>
      <Form onSubmit={handleSubmit}>
        <Paragraph color="subtle" mb="100">
          Device token
        </Paragraph>

        <LastTestedTokenField
          mb="100"
          lastTested={lastTested}
          isChecked={matchesLastTestedRecipient(lastTested, { recipient, platform: selectedPlatform, sandbox })}
          isDisabled={isLocked}
          onChange={selectLastTested}
        />

        <InlineStack alignBlock="end" alignInline="space-between">
          <Box mr="100" flexGrow="1" maxW="full" minW="0">
            <RecipientFields
              canChoosePlatform={canChoosePlatform}
              platform={selectedPlatform}
              recipient={recipient}
              isDisabled={isLocked}
              isInvalid={hasRecipientError(errors)}
              onPlatformChange={setSelectedPlatform}
              onRecipientChange={setRecipient}
            />
          </Box>
          <Box>
            <SendTestButton
              blockedReason={blockedReason}
              isDisabled={isDisabled}
              isSending={isSending}
              hasRunBefore={!sendTest.isIdle}
            />
          </Box>
        </InlineStack>

        <InlineStack alignBlock="center" alignInline="space-between" mt="150">
          <Link href={DEVICE_TOKEN_HELP_URL} isExternal={true} variant="secondary" icon="help">
            How to find your device tokens
          </Link>
          <Box>
            <SendStatus
              isSending={isSending}
              hasFailed={sendTest.isError || errors.length > 0}
              hasSucceeded={sendTest.isSuccess}
            />
          </Box>
        </InlineStack>
      </Form>

      <InlineStack alignInline="center">
        <Paragraph color="subtle" mt="200" fontStyle="italic" text="product-pxs-secondary">
          Your test push will look like this.
        </Paragraph>
      </InlineStack>

      <PhonePreview title={PUSH_TEST_TITLE} body={PUSH_TEST_BODY} />
    </Box>
  );
}

/** The unsaved credential to test with, when there is one to supply. */
async function resolveCredentials(getCredentials: (() => Promise<string>) | null): Promise<string | undefined> {
  return getCredentials == null ? undefined : getCredentials();
}

/** What to remember after a successful send. Sandbox is only recorded when used. */
function rememberedRecipient(recipient: string, platform: PushPlatform | null, sandbox: boolean) {
  const tested: LastTestedRecipient = { id: recipient.trim(), platform };
  if (sandbox) {
    tested.sandbox = true;
  }
  return tested;
}

/** The device type after picking (or clearing) the remembered token. */
function nextPlatform(
  chosen: LastTestedRecipient | null,
  canChoosePlatform: boolean,
  current: PushPlatform | null,
): PushPlatform | null {
  if (!canChoosePlatform) {
    return current;
  }
  return chosen?.platform ?? null;
}

/**
 * Why the test cannot be sent yet, or null when it can.
 *
 * An unconfirmed account cannot send at all, so that outranks anything the form
 * is still missing — there is no point telling someone to pick a device type
 * when nothing would go out either way.
 */
function sendBlockedReason(
  needsEmailConfirmation: boolean,
  platform: PushPlatform | null,
  recipient: string,
  configuredPlatformCount: number,
): string | null {
  if (needsEmailConfirmation) {
    return 'Verify your email address to send a test.';
  }
  return formBlockedReason(platform, recipient, configuredPlatformCount);
}

/** What the form itself is still missing, in the order someone fills it in. */
function formBlockedReason(
  platform: PushPlatform | null,
  recipient: string,
  configuredPlatformCount: number,
): string | null {
  if (platform == null) {
    return configuredPlatformCount === 0 ? 'Enable push actions.' : 'Select a device type.';
  }
  if (isEmptyString(recipient)) {
    return 'Enter a token.';
  }
  return null;
}

/** Whether the API blamed the device token itself rather than the configuration. */
function hasRecipientError(errors: PushApiError[]): boolean {
  return errors.some((error) => error.source?.pointer === '/data/attributes/recipient');
}

type SendTestButtonProps = {
  /** Why the test cannot be sent, shown on hover; null when it can. */
  blockedReason: string | null;
  isDisabled: boolean;
  isSending: boolean;
  hasRunBefore: boolean;
};

function SendTestButton({ blockedReason, isDisabled, isSending, hasRunBefore }: SendTestButtonProps) {
  const isBlocked = blockedReason != null;

  return (
    <Tooltip isCustom={true} content={blockedReason ?? undefined} isDisabled={isDisabled || isSending || !isBlocked}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          type="submit"
          variant="primary"
          size="sm"
          isDisabled={isDisabled || isBlocked}
          isLoading={isSending}
        >
          {hasRunBefore ? 'Send again' : 'Send test'}
        </Button>
      )}
    </Tooltip>
  );
}

type LastTestedTokenFieldProps = {
  lastTested: LastTestedRecipient | null;
  isChecked: boolean;
  isDisabled: boolean;
  onChange: (checked: boolean) => void;
} & StackProps;

function LastTestedTokenField({ lastTested, isChecked, isDisabled, onChange, ...boxProps }: LastTestedTokenFieldProps) {
  if (lastTested == null) {
    return null;
  }

  return (
    <Stack gap="150" {...boxProps}>
      <Checkbox
        label="Last tested token"
        name="last_tested_token"
        isChecked={isChecked}
        isDisabled={isDisabled}
        onCheckedChange={onChange}
      />
      <Paragraph color="subtle">A custom token</Paragraph>
    </Stack>
  );
}

type RecipientFieldsProps = {
  canChoosePlatform: boolean;
  platform: PushPlatform | null;
  recipient: string;
  isDisabled: boolean;
  isInvalid: boolean;
  onPlatformChange: (platform: PushPlatform | null) => void;
  onRecipientChange: (recipient: string) => void;
};

function RecipientFields({
  canChoosePlatform,
  platform,
  recipient,
  isDisabled,
  isInvalid,
  onPlatformChange,
  onRecipientChange,
}: RecipientFieldsProps) {
  const tokenField = (
    <TextField
      size="sm"
      label={canChoosePlatform ? 'Device token' : 'Recipient'}
      placeholder="Device token"
      value={recipient}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      onInput={(event) => onRecipientChange(event.target.value)}
    />
  );

  if (!canChoosePlatform) {
    return tokenField;
  }

  return (
    <Grid gap="100" columns="100px 1fr">
      <Select
        size="sm"
        label="Device"
        placeholder="Select…"
        value={platform}
        isDisabled={isDisabled}
        options={PLATFORM_OPTIONS}
        onChange={(value) => onPlatformChange(value === 'ios' || value === 'android' ? value : null)}
      />
      {tokenField}
    </Grid>
  );
}

type SendStatusProps = {
  isSending: boolean;
  hasFailed: boolean;
  hasSucceeded: boolean;
};

function SendStatus({ isSending, hasFailed, hasSucceeded }: SendStatusProps) {
  if (hasFailed) {
    return <Text color="critical">Test failed to send.</Text>;
  }
  if (isSending) {
    return <Spinner />;
  }
  if (hasSucceeded) {
    return <Text color="success">Test sent.</Text>;
  }
  return null;
}
